import React, { Fragment, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import pickBy from 'lodash.pickby';
import { studentType } from '../../../constants/prop-types';
import {
    getAllStudentResults,
    getStudentResults,
    groupStudentResultsByApplication,
    getValidOfficialBurstResults,
} from '../../../common/utils/studentsListHelpers';
import PMRecommendation from './PMRecommendation';
import getRecommendedMeasures, {
    getNextFormUidForAllGrades,
    getRecommendationStatuses,
} from '../../../common/recommendation-rules';
import { useAppContext } from '../../../common/contexts/AppContext';
import PMHistory from './PMHistory';
import { PERIOD } from '../../../constants/enrollment';
import {
    PERIOD_KEY,
    IS_COMPLETE_KEY,
    MEASURE_KEY,
    ACCESS_LINK_KEY,
} from '../../../constants/jsonKeys';

import {
    PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION,
    PM_MEASURES_BY_APPLICATION,
    SUPPORTED_GRADES_BY_APP,
} from '../../../constants/assessments';
import { constructStartPMAssessmentLink } from '../../../common/navigation/navigation';
import {
    BURST,
    DIBELS8,
    IDEL,
    IDEL2,
    APPLICATIONS_GROUPS,
    ESPANOL,
} from '../../../constants/applications';
import TableHeader from '../table-header/TableHeader';
import IdelLink from '../idel-summary/IdelLink';
import { SECTION_NAMES } from '../../../common/utils/studentsSortingHelpers';
import { useAppConfigContext } from '../../../common/contexts/AppConfigContext';
import { useFeatureToggleContext } from '../../../common/contexts/FeatureToggleContext';
import { usePopupContext } from '../../../common/contexts/PopupContext';

export const PM_RECOMMENDATION_COLUMNS = ['Skill', 'Form', 'Assess'];
export const PM_RECOMMENDATION_COLUMNS_IDEL_ONLY = ['Skill'];

const isIncompletePMResult = result =>
    get(result, PERIOD_KEY) === PERIOD.PM &&
    get(result, IS_COMPLETE_KEY) !== undefined &&
    get(result, IS_COMPLETE_KEY) === false;

const getLabel = (isRecommended, hasPausedResult, sourceApplicationName) => {
    if (sourceApplicationName === BURST) {
        return isRecommended ? 'Required' : '';
    }
    return isRecommended || hasPausedResult ? 'Recommended' : '';
};

const getPmStatusesByApplication = (
    applicationName,
    sourceApplicationName,
    applicationResults,
    recommendedMeasures,
    recommendationStatusesByApp,
    classId,
    student,
    nextFormUids,
    formsMap,
) => {
    const skillAndMeasureNames = get(
        PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION,
        applicationName,
        [],
    );
    const skillStatuses = skillAndMeasureNames.reduce(
        (applicationSkills, { skillName, measureName }) => {
            const incompletePMResult = get(applicationResults, applicationName, [])
                .filter(result => get(result, MEASURE_KEY) === measureName)
                .find(isIncompletePMResult);
            const hasPausedResult = Boolean(incompletePMResult);
            const isRecommended = recommendedMeasures[sourceApplicationName].includes(measureName);
            const recommendationStatus = recommendationStatusesByApp[applicationName][measureName];
            const formUid = isRecommended
                ? get(nextFormUids, [applicationName, measureName, student.grade], '')
                : '';
            const label = getLabel(isRecommended, hasPausedResult, sourceApplicationName);
            const params = {
                measure: measureName,
                formUid,
            };
            const navParams =
                applicationName === BURST
                    ? {
                          ...params,
                          probeConfig: formsMap[formUid],
                      }
                    : params;
            const accessLink = hasPausedResult
                ? get(incompletePMResult, ACCESS_LINK_KEY)
                : constructStartPMAssessmentLink(applicationName, classId, student.id, navParams);

            return [
                ...applicationSkills,
                {
                    skillName,
                    measureName,
                    hasPausedResult,
                    accessLink,
                    isRecommended,
                    recommendationStatus,
                    formUid,
                    label,
                },
            ];
        },
        [],
    );
    return {
        [applicationName]: skillStatuses,
    };
};

export const getStudentPMStatus = (
    studentResults,
    applications,
    classId,
    student,
    formsMap,
    studentInstruction,
    recommendedMeasures,
    recommendationStatusesByApp,
    nextFormUids,
) => {
    const applicationResults = groupStudentResultsByApplication(studentResults);
    const hasBurstInstructions = applications.includes(BURST) && studentInstruction !== null;

    return applications.reduce((pmStatusesByApp, applicationName) => {
        // D8 recommendations are taken from Burst instructions when they are present
        const recommendationSourceApp =
            applicationName === DIBELS8 && hasBurstInstructions ? BURST : applicationName;
        return {
            ...pmStatusesByApp,
            ...getPmStatusesByApplication(
                applicationName,
                recommendationSourceApp,
                applicationResults,
                recommendedMeasures,
                recommendationStatusesByApp,
                classId,
                student,
                nextFormUids,
                formsMap,
            ),
        };
    }, {});
};

export const getValidOfficialBurstResultsByPeriod = resultsByPeriod => {
    const filteredResultsByPeriod = {};
    Object.keys(resultsByPeriod).forEach(period => {
        filteredResultsByPeriod[period] = getValidOfficialBurstResults(
            resultsByPeriod[period],
            period,
        );
    });
    return filteredResultsByPeriod;
};

export const PMSummary = ({ student, classId }) => {
    const {
        staff: { id: staffId },
        formsMap,
        bmWindow,
        cutscores,
        allResults,
        studentsGoals,
        studentsZoG,
        nextBMWindow,
        selectedAppGroup,
    } = useAppContext();
    const { appPmConfigs: appConfigs, isLecturaLicensed } = useAppConfigContext();
    const { isLecturaPMEnabled } = useFeatureToggleContext();
    const {
        actions: { showNoIDELSupportPopup },
    } = usePopupContext();

    const licensedPMMeasuresByApp = useMemo(
        () =>
            pickBy(PM_MEASURES_BY_APPLICATION, (measures, appName) => {
                if (appName === IDEL2 && !(isLecturaLicensed && isLecturaPMEnabled)) {
                    return false;
                }
                const isInCurrentAppGroup = APPLICATIONS_GROUPS[selectedAppGroup].includes(appName);
                const isLicensedApp = appConfigs.hasOwnProperty(appName);
                const hasPMMeasures = measures.length > 0;
                return isInCurrentAppGroup && isLicensedApp && hasPMMeasures;
            }),
        [appConfigs, isLecturaLicensed, isLecturaPMEnabled, selectedAppGroup],
    );

    const studentResultsAllApps = getStudentResults(allResults, student.id, getAllStudentResults);
    const studentResults = Object.keys(studentResultsAllApps)
        .filter(app => licensedPMMeasuresByApp.hasOwnProperty(app))
        .reduce(
            (res, app) => ({
                ...res,
                [app]:
                    app === BURST
                        ? getValidOfficialBurstResultsByPeriod(studentResultsAllApps[app])
                        : studentResultsAllApps[app],
            }),
            {},
        );
    const studentInstruction = get(
        student,
        `appStatuses.[${BURST}][${PERIOD.PM}].instruction`,
        null,
    );

    const recommendedMeasures = getRecommendedMeasures(
        student,
        studentResults,
        Object.keys(licensedPMMeasuresByApp),
        bmWindow.period,
        studentInstruction,
        formsMap,
    );

    const recommendationStatusesByApp = getRecommendationStatuses(
        student,
        studentResults,
        Object.keys(licensedPMMeasuresByApp),
        recommendedMeasures,
        bmWindow.period,
        nextBMWindow,
        cutscores,
        studentsGoals,
        studentsZoG,
        formsMap,
    );
    const nextFormUids = useMemo(
        () => getNextFormUidForAllGrades(studentResults, formsMap, licensedPMMeasuresByApp),
        [formsMap, licensedPMMeasuresByApp, studentResults],
    );

    const skillsStatusesByApplication = getStudentPMStatus(
        studentResults,
        Object.keys(licensedPMMeasuresByApp),
        classId,
        student,
        formsMap,
        studentInstruction,
        recommendedMeasures,
        recommendationStatusesByApp,
        nextFormUids,
    );

    // PM details (whole History section and Recommendation except IDEL1 link) are available
    // if there are licensed apps that can report PM on Dashboard
    const arePMDetailsAvailable = Object.keys(licensedPMMeasuresByApp).length > 0;

    const isIdel1Enabled = selectedAppGroup === ESPANOL && appConfigs.hasOwnProperty(IDEL);

    let tableHeaderColumns = null;
    if (arePMDetailsAvailable) {
        tableHeaderColumns = PM_RECOMMENDATION_COLUMNS;
    } else if (isIdel1Enabled) {
        tableHeaderColumns = PM_RECOMMENDATION_COLUMNS_IDEL_ONLY;
    }

    return (
        <div className='dashboard-pm-summary'>
            {tableHeaderColumns && (
                <TableHeader columns={tableHeaderColumns} cssClass="recommended" />
            )}
            {isIdel1Enabled && SUPPORTED_GRADES_BY_APP[IDEL].includes(student.grade) && (
                <Fragment>
                    <IdelLink onClickHandle={showNoIDELSupportPopup} label={SECTION_NAMES.IDEL} />
                    {arePMDetailsAvailable && <div className='dashboard-idel-link-separator' />}
                </Fragment>
            )}
            {arePMDetailsAvailable && (
                <Fragment>
                    <PMRecommendation
                        student={student}
                        skillsStatusesByApplication={skillsStatusesByApplication}
                        hasInterventionInstruction={studentInstruction !== null}
                        nextFormUids={nextFormUids}
                        classId={classId}
                    />
                    <PMHistory
                        results={studentResults}
                        student={student}
                        classId={classId}
                        staffId={staffId}
                    />
                </Fragment>
            )}
        </div>
    );
};

PMSummary.propTypes = {
    classId: PropTypes.string.isRequired,
    student: studentType.isRequired,
};

export default memo(PMSummary);
